import React, { useMemo } from 'react';
import { buildPath, extent, linearScale, padDomain, Point } from './chartUtils';

interface SparklineProps {
  /** Lap times in seconds, oldest first. */
  values: number[];
  width?: number;
  height?: number;
  /** Accessible description; also the browser tooltip. */
  title?: string;
}

/**
 * A pace trace small enough to sit in a grid cell — no axes, no library, just
 * the shape of the last few laps and where the most recent one landed.
 */
export const Sparkline: React.FC<SparklineProps> = ({
  values,
  width = 64,
  height = 18,
  title,
}) => {
  const geometry = useMemo(() => {
    const clean = values.filter((v) => Number.isFinite(v) && v > 0);
    if (clean.length < 2) return null;

    const span = extent(clean);
    if (!span) return null;

    const pad = 2;
    const x = linearScale([0, clean.length - 1], [pad, width - pad]);
    // Inverted: a quicker lap is a lower number and should sit higher.
    const y = linearScale(padDomain(span, 0.15), [height - pad, pad]);

    const points: Point[] = clean.map((v, i) => ({ x: x(i), y: y(v) }));
    return { path: buildPath(points), last: points[points.length - 1] };
  }, [values, width, height]);

  if (!geometry) return <span className="spark-empty">—</span>;

  return (
    <svg
      className="spark"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={title ?? 'Recent lap times'}
    >
      {title && <title>{title}</title>}
      <path className="spark-line" d={geometry.path} fill="none" />
      <circle
        className="spark-head"
        cx={geometry.last.x}
        cy={geometry.last.y}
        r={1.8}
      />
    </svg>
  );
};

export default Sparkline;
